import { useState } from "react";
import { Money } from "@cliqsuite/ui";
import { formatMoney } from "../lib/format";
import type { CartLine, OrderType } from "../types";
import "../ticket.css";

// The open ticket (canvas v2-d-sell*): ticket tabs, order type, lines, totals, park/send/charge.

// "counter" (order_type in the DB) is an immediate-pay sale: "Quick sale".
const ORDER_TYPES: Array<{ key: OrderType; label: string }> = [
  { key: "counter", label: "Quick sale" },
  { key: "dine_in", label: "Dine-in" },
  { key: "pickup", label: "Pickup" },
];

export const MAX_OPEN_TICKETS = 3;

export interface TicketTab {
  id: string;
  label: string;
  active: boolean;
}

export interface CartPanelProps {
  isDesktop: boolean;
  staffName: string;
  startedAt: string;
  tabs: TicketTab[];
  onSwitchTab: (id: string) => void;
  onNewTicket: () => void;
  onMoveTab: (id: string, toIndex: number) => void;
  orderType: OrderType;
  onChangeOrderType: (type: OrderType) => void;
  /** Dine-in only exists with the restaurant module. */
  hasRestaurant: boolean;
  selectedTable: { id: string; label: string; seats: number } | null;
  onChooseTable: () => void;
  pickupName: string;
  onPickupName: (v: string) => void;
  orderNote: string;
  onOrderNoteChange: (v: string) => void;
  lines: CartLine[];
  onInc: (key: string) => void;
  onDec: (key: string) => void;
  onRemoveLine: (key: string) => void;
  onLineNote: (key: string, note: string) => void;
  onAddOnsLine: (key: string) => void;
  onClear: () => void;
  currency: { code: string; precision: 0 | 2 | 3 };
  subtotal: number;
  employeeDiscount: number;
  employeeDiscountPercent: number;
  employeeDiscountAvailable: boolean;
  /** The store's configured staff-discount rate, shown on the switch before it's on. */
  employeeDiscountRate: number;
  onToggleEmployeeDiscount: () => void;
  tax: number;
  total: number;
  taxLabel: string;
  onCharge: () => void;
  chargeDisabled: boolean;
  onSend: () => void;
  sendDisabled: boolean;
  sendLabel: string;
  lastOrder: { label: string; summary: string } | null;
  onRepeatLast: () => void;
}

const Icon = ({ name, size = 18, color, fill }: { name: string; size?: number; color?: string; fill?: boolean }) => (
  <span className="ms" aria-hidden="true" style={{ fontSize: size, color, fontVariationSettings: fill ? "'FILL' 1" : undefined }}>
    {name}
  </span>
);

export function CartPanel(p: CartPanelProps) {
  const fmt = (v: number) => formatMoney(v, { currencyCode: p.currency.code, precision: p.currency.precision });
  const hasItems = p.lines.length > 0;
  const [openLine, setOpenLine] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const canNew = p.tabs.length < MAX_OPEN_TICKETS;
  const kitchenType = p.orderType !== "counter";

  return (
    <section aria-label="Ticket" className={`pos-ticket${p.isDesktop ? "" : " phone"}`}>
      <div role="tablist" aria-label="Open tickets" className="pos-tabs">
        {p.tabs.map((t, i) => (
          <button
            key={t.id}
            role="tab"
            aria-selected={t.active}
            className={`pos-tab${t.active ? " on" : ""}${dragId === t.id ? " drag" : ""}`}
            draggable
            onDragStart={(e) => {
              setDragId(t.id);
              e.dataTransfer.effectAllowed = "move";
            }}
            onDragOver={(e) => {
              if (!dragId || dragId === t.id) return;
              e.preventDefault();
              p.onMoveTab(dragId, i);
            }}
            onDragEnd={() => setDragId(null)}
            onClick={() => p.onSwitchTab(t.id)}
          >
            <Icon name={t.active ? "receipt" : "pause_circle"} size={t.active ? 18 : 16} color={t.active ? "var(--coral)" : "var(--text2)"} fill={t.active} />
            {t.label}
          </button>
        ))}
        {canNew && (
          <button className="pos-tab add" aria-label="New ticket" onClick={p.onNewTicket}>
            <Icon name="add" size={20} color="var(--ink)" />
          </button>
        )}
      </div>

      {/* One white sheet from the order to the charge key; the tabs above it stand on the frame (canvas v2-d-sell). */}
      <div className="pos-ticket-sheet">
      <div className="pos-ticket-body">
        <div className="pos-ticket-head">
          <div>
            {p.staffName} · {p.startedAt}
          </div>
          {p.orderType === "dine_in" && p.selectedTable && (
            <button className="pos-table-tag" onClick={p.onChooseTable} aria-label={`${p.selectedTable.label}, change table`}>
              <span className="ms" aria-hidden="true">table_restaurant</span>
              {p.selectedTable.label}
            </button>
          )}
          {hasItems && (
            <button className="pos-sq-btn" aria-label="Clear ticket" onClick={p.onClear}>
              <Icon name="delete_sweep" size={19} color="var(--text2)" />
            </button>
          )}
        </div>

        <div role="radiogroup" aria-label="Order type" className="pos-seg">
          {ORDER_TYPES.filter((ot) => ot.key !== "dine_in" || p.hasRestaurant).map((ot) => (
            <button key={ot.key} type="button" role="radio" aria-checked={p.orderType === ot.key} className={p.orderType === ot.key ? "on" : ""} onClick={() => p.onChangeOrderType(ot.key)}>
              {ot.label}
            </button>
          ))}
        </div>

        {p.orderType === "dine_in" && !p.selectedTable && (
          <button className="pos-pick-table" onClick={p.onChooseTable}>
            <Icon name="table_restaurant" size={20} color="var(--text2)" />
            <span>
              <b>Choose a table</b>
              <small>Pick from the floor plan</small>
            </span>
            <Icon name="chevron_right" size={20} color="var(--muted)" />
          </button>
        )}
        {kitchenType && (
          <div className="pos-ticket-fields">
            {p.orderType === "pickup" && (
              <label>
                <Icon name="person" color="var(--text2)" />
                <input aria-label="Customer name" placeholder="Customer name (optional)" value={p.pickupName} onChange={(e) => p.onPickupName(e.target.value)} />
              </label>
            )}
            <label>
              <Icon name="sticky_note_2" color="var(--amber)" />
              <input aria-label="Note for this order" placeholder="Note for this order (optional)" value={p.orderNote} onChange={(e) => p.onOrderNoteChange(e.target.value)} />
            </label>
          </div>
        )}

        <div className="pos-lines">
          {!hasItems && (
            <div className="pos-ticket-empty">
              <div>
                <Icon name="receipt_long" size={30} color="var(--coral)" />
              </div>
              <b>Nothing on this ticket yet</b>
              <small>Tap an item, or scan a barcode.</small>
              {p.lastOrder && (
                <button className="pos-repeat" onClick={p.onRepeatLast}>
                  <Icon name="replay" size={20} color="var(--text2)" />
                  <span>
                    <b>Repeat last order {p.lastOrder.label}</b>
                    <small>{p.lastOrder.summary}</small>
                  </span>
                </button>
              )}
            </div>
          )}
          {p.lines.map((l) => {
            const open = openLine === l.key;
            const detail = l.selections?.length
              ? l.selections.map((s) => `${s.quantity && s.quantity > 1 ? `${s.quantity}× ` : ""}${s.label}${s.priceDelta > 0 ? ` +${fmt(s.priceDelta * (s.quantity ?? 1))}` : ""}`).join(", ")
              : l.variantLabel ?? null;
            return (
              <div key={l.key} className={`pos-line${open ? " open" : ""}`}>
                <button className="pos-line-main" aria-expanded={open} onClick={() => setOpenLine(open ? null : l.key)}>
                  <span className="pos-line-qty num">{l.qty}×</span>
                  <span className="pos-line-text">
                    <b>{l.name}</b>
                    {detail && <small>{detail}</small>}
                    {l.notes && !open && (
                      <em>
                        <Icon name="sticky_note_2" size={14} color="var(--amber)" />
                        {l.notes}
                      </em>
                    )}
                  </span>
                  <span className="num pos-line-total">{fmt(l.unitPrice * l.qty)}</span>
                </button>
                {open && (
                  <div className="pos-line-edit">
                    <div className="pos-line-tools">
                      <button aria-label="One less" onClick={() => (l.qty > 1 ? p.onDec(l.key) : p.onRemoveLine(l.key))}>
                        <Icon name="remove" size={20} color="var(--ink)" />
                      </button>
                      <span className="num">{l.qty}</span>
                      <button aria-label="One more" onClick={() => p.onInc(l.key)}>
                        <Icon name="add" size={20} color="var(--ink)" />
                      </button>
                      <span style={{ flex: 1 }} />
                      <button className="wide" onClick={() => p.onAddOnsLine(l.key)}>
                        <Icon name="add_card" size={17} color="var(--ink)" />
                        Add-on or charge
                      </button>
                      <button aria-label="Remove line" className="danger" onClick={() => p.onRemoveLine(l.key)}>
                        <Icon name="delete" size={19} color="var(--coral)" />
                      </button>
                    </div>
                    <label className="pos-line-note">
                      <Icon name="sticky_note_2" size={17} color="var(--amber)" />
                      <input aria-label="Kitchen note" placeholder="Note for the kitchen" value={l.notes ?? ""} onChange={(e) => p.onLineNote(l.key, e.target.value)} />
                    </label>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {hasItems && (
          <div className="pos-sums">
            <div>
              <span>Subtotal</span>
              <span className="num">{fmt(p.subtotal)}</span>
            </div>
            {p.orderType === "counter" && p.employeeDiscountAvailable && (
              <div>
                <button role="switch" aria-checked={p.employeeDiscountPercent > 0} className="pos-switch-row" onClick={p.onToggleEmployeeDiscount}>
                  <span className={`pos-switch${p.employeeDiscountPercent > 0 ? " on" : ""}`}>
                    <span />
                  </span>
                  Staff discount {p.employeeDiscountPercent || p.employeeDiscountRate}%
                </button>
                <span className="num" style={{ color: p.employeeDiscountPercent > 0 ? "var(--ok)" : "var(--muted)" }}>
                  {p.employeeDiscountPercent > 0 ? `−${fmt(p.employeeDiscount)}` : "Off"}
                </span>
              </div>
            )}
            <div>
              <span>{p.taxLabel}</span>
              <span className="num">{fmt(p.tax)}</span>
            </div>
            <div className="total">
              <span>Total</span>
              <span className="num">{fmt(p.total)}</span>
            </div>
          </div>
        )}

        {hasItems && (
          <div className="pos-ticket-actions">
            <button disabled={!canNew} onClick={p.onNewTicket}>
              <Icon name="pause_circle" color="var(--ink)" />
              Park{" "}
              <span className="num">
                {p.tabs.length} of {MAX_OPEN_TICKETS} open
              </span>
            </button>
            {kitchenType && p.hasRestaurant && (
              <button disabled={p.sendDisabled} onClick={p.onSend}>
                <Icon name="soup_kitchen" color="var(--ink)" />
                {p.sendLabel}
              </button>
            )}
          </div>
        )}
      </div>
      <div aria-hidden="true" className="pos-zigzag" />
      <button className="pos-charge" disabled={p.chargeDisabled} onClick={p.onCharge}>
        <span>
          <small>Charge</small>
          {hasItems && <b><Money amount={p.total} opts={{ currencyCode: p.currency.code, precision: p.currency.precision }} /></b>}
        </span>
        <span className="pos-charge-go">
          <Icon name="arrow_forward" size={24} color="#fff" />
        </span>
      </button>
      </div>
    </section>
  );
}
